import React, { useEffect, useMemo, useRef, useState } from "react";
import { listNews, listNewsTags } from "../../api/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import PageBackButton from "../../components/PageBackButton";
import { Check, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, X } from "lucide-react";

/** Strip HTML tags and decode common entities */
function stripHtml(html) {
  if (!html) return "";
  // Use a temporary element to parse HTML and extract text
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

/** Sanitize HTML for safe rendering — keep only basic formatting tags */
function sanitizeHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  // Remove script/style/iframe tags
  tmp.querySelectorAll("script, style, iframe, object, embed, form, input").forEach((el) => el.remove());
  return tmp.innerHTML;
}

function formatTime(ts) {
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts || "-";
  }
}

function labelize(value) {
  return String(value || "").replace(/_/g, " ");
}

function hasNumber(value) {
  return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
}

function formatMetric(value, { digits = 2, signed = false } = {}) {
  if (!hasNumber(value)) return null;
  const numeric = Number(value);
  const fixed = numeric.toFixed(digits);
  const trimmed = fixed.replace(/\.00$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
  return signed && numeric > 0 ? `+${trimmed}` : trimmed;
}

function compactList(values, limit = 3) {
  const items = Array.isArray(values) ? values.filter(Boolean) : [];
  if (!items.length) return null;
  const visible = items.slice(0, limit).map((item) => labelize(item));
  const remaining = items.length - visible.length;
  return remaining > 0 ? `${visible.join(", ")} +${remaining}` : visible.join(", ");
}

function sentimentTone(direction) {
  const normalized = String(direction || "").toLowerCase();
  if (normalized === "positive") return "bg-emerald-500/15 text-emerald-300 border-emerald-400/30";
  if (normalized === "negative") return "bg-rose-500/15 text-rose-300 border-rose-400/30";
  if (normalized === "neutral") return "bg-sky-500/15 text-sky-300 border-sky-400/30";
  return "bg-secondary/40 text-textSecondary border-border/40";
}

function ValuePills({ values, maxVisible = 4, tone = "accent" }) {
  const items = Array.isArray(values) ? values.filter(Boolean) : [];
  if (items.length === 0) {
    return <span className="text-textSecondary text-xs">-</span>;
  }

  const visible = items.slice(0, maxVisible);
  const remaining = items.length - visible.length;
  const toneClass = tone === "primary"
    ? "bg-primary/10 text-primary border-primary/20"
    : "bg-accent/20 text-accent-foreground border-accent/20";

  return (
    <div className="flex flex-wrap gap-1 max-w-[220px]">
      {visible.map((item) => (
        <span
          key={item}
          className={`px-1.5 py-0.5 rounded border text-[10px] whitespace-nowrap ${toneClass}`}
        >
          {labelize(item)}
        </span>
      ))}
      {remaining > 0 ? (
        <span className="px-1.5 py-0.5 rounded border border-border/40 text-[10px] text-textSecondary whitespace-nowrap">
          +{remaining}
        </span>
      ) : null}
    </div>
  );
}

const TAG_OPTIONS_PAGE_SIZE = 60;

function TagMultiSelect({ availableTags, selectedTags, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(TAG_OPTIONS_PAGE_SIZE);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  const selectedSet = useMemo(() => new Set(selectedTags), [selectedTags]);
  const sortedTags = useMemo(
    () => Array.from(new Set((availableTags || []).filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b))),
    [availableTags]
  );

  const filteredTags = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedTags;

    return sortedTags
      .map((tag, index) => {
        const label = labelize(tag).toLowerCase();
        const value = String(tag).toLowerCase();
        const rank = value.startsWith(q) ? 0 : label.startsWith(q) ? 1 : value.includes(q) || label.includes(q) ? 2 : null;
        return { tag, index, rank };
      })
      .filter((item) => item.rank !== null)
      .sort((a, b) => (a.rank - b.rank) || (a.index - b.index))
      .map((item) => item.tag);
  }, [query, sortedTags]);

  const visibleTags = filteredTags.slice(0, visibleCount);
  const hasMoreTags = visibleCount < filteredTags.length;
  const selectedPreview = selectedTags.slice(0, 2).map(labelize).join(", ");
  const selectedOverflow = Math.max(0, selectedTags.length - 2);
  const selectedChipOverflow = Math.max(0, selectedTags.length - 8);

  useEffect(() => {
    setVisibleCount(TAG_OPTIONS_PAGE_SIZE);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [query, open]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const toggleTag = (tag) => {
    if (selectedSet.has(tag)) {
      onChange(selectedTags.filter((selectedTag) => selectedTag !== tag));
      return;
    }
    onChange([...selectedTags, tag]);
  };

  const removeTag = (tag) => {
    onChange(selectedTags.filter((selectedTag) => selectedTag !== tag));
  };

  const handleListScroll = (event) => {
    const el = event.currentTarget;
    if (!hasMoreTags || el.scrollHeight - el.scrollTop - el.clientHeight > 48) return;
    setVisibleCount((count) => Math.min(count + TAG_OPTIONS_PAGE_SIZE, filteredTags.length));
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className="flex min-h-[38px] w-full items-center justify-between gap-3 rounded-md border border-borderDefault bg-backgroundInput px-3 py-2 text-left text-sm shadow-sm transition-colors hover:bg-secondary/20 focus:outline-none focus:ring-2 focus:ring-primary"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={`min-w-0 flex-1 truncate ${selectedTags.length ? "text-textPrimary" : "text-textSecondary"}`}>
          {selectedTags.length
            ? `${selectedTags.length} tag${selectedTags.length === 1 ? "" : "s"}: ${selectedPreview}${selectedOverflow ? ` +${selectedOverflow}` : ""}`
            : "Select tags"}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-textSecondary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {selectedTags.length > 0 ? (
        <div className="mt-2 flex max-w-full flex-wrap items-center gap-1.5">
          {selectedTags.slice(0, 8).map((tag) => (
            <span
              key={tag}
              className="inline-flex max-w-[180px] items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-xs text-primary"
              title={labelize(tag)}
            >
              <span className="truncate">{labelize(tag)}</span>
              <button
                type="button"
                className="rounded-full p-0.5 hover:bg-primary/20 focus:outline-none focus:ring-1 focus:ring-primary"
                aria-label={`Remove ${labelize(tag)}`}
                onClick={() => removeTag(tag)}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {selectedChipOverflow > 0 ? (
            <span className="rounded-full border border-border/40 px-2 py-0.5 text-xs text-textSecondary">
              +{selectedChipOverflow}
            </span>
          ) : null}
          <button
            type="button"
            className="px-1.5 py-0.5 text-xs text-textSecondary hover:text-textPrimary"
            onClick={() => onChange([])}
          >
            Clear tags
          </button>
        </div>
      ) : null}

      {open ? (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-md border border-borderDefault bg-backgroundSurface shadow-xl">
          <div className="border-b border-borderDefault p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-textSecondary" />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search tags"
                className="w-full rounded border border-borderDefault bg-backgroundInput py-2 pl-8 pr-3 text-sm text-textPrimary placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div
            ref={listRef}
            role="listbox"
            aria-multiselectable="true"
            className="max-h-72 overflow-y-auto overscroll-contain py-1"
            onScroll={handleListScroll}
          >
            {visibleTags.length === 0 ? (
              <div className="px-3 py-3 text-sm text-textSecondary">No matching tags</div>
            ) : (
              visibleTags.map((tag) => {
                const active = selectedSet.has(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                      active ? "bg-primary/10 text-primary" : "text-textPrimary hover:bg-secondary/30"
                    }`}
                    onClick={() => toggleTag(tag)}
                  >
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      active ? "border-primary bg-primary text-primary-foreground" : "border-borderDefault"
                    }`}>
                      {active ? <Check className="h-3 w-3" /> : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{labelize(tag)}</span>
                  </button>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-between border-t border-borderDefault px-3 py-2 text-xs text-textSecondary">
            <span>{Math.min(visibleCount, filteredTags.length).toLocaleString()} of {filteredTags.length.toLocaleString()} tags</span>
            <span>{hasMoreTags ? "Scroll for more" : selectedTags.length ? `${selectedTags.length} selected` : "No tags selected"}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function getDefaultDateRange() {
  const now = new Date();
  const monthAgo = new Date(now);
  monthAgo.setDate(now.getDate() - 30);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(monthAgo), to: fmt(now) };
}

export default function NewsPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [articles, setArticles] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [source, setSource] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [search, setSearch] = useState("");
  const [availableTags, setAvailableTags] = useState([]);

  useEffect(() => {
    const { from, to } = getDefaultDateRange();
    setDateFrom(from);
    setDateTo(to);
    fetchData({ date_from: from, date_to: to });
    // Load available tags
    listNewsTags().then((tags) => {
      if (Array.isArray(tags)) {
        setAvailableTags(Array.from(new Set(tags.filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b))));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (overrides = {}) => {
    try {
      setLoading(true);
      setError(null);
      const currentPage = overrides.page ?? page;
      const currentTags = overrides.tags ?? selectedTags;
      const params = {
        date_from: overrides.date_from ?? dateFrom,
        date_to: overrides.date_to ?? dateTo,
        source: overrides.source ?? source,
        tags: currentTags.length > 0 ? currentTags.join(",") : undefined,
        search: (overrides.search ?? search) || undefined,
        page: currentPage,
        page_size: pageSize,
      };
      const res = await listNews(params);
      // Paginated response: { count, next, previous, results }
      if (res && typeof res.count === "number" && Array.isArray(res.results)) {
        setArticles(res.results);
        setTotalCount(res.count);
      } else {
        // Fallback for non-paginated response
        const items = Array.isArray(res) ? res : [];
        setArticles(items);
        setTotalCount(items.length);
      }
    } catch (e) {
      setError(e.message || "Failed to load news");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSource("");
    setSelectedTags([]);
    setSearch("");
    setPage(1);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
    setExpandedIds(new Set());
    fetchData({ page: p });
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start gap-3">
        <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
        <div>
          <h1 className="text-2xl font-semibold">Financial News</h1>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm mb-1">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm mb-1">Source</label>
            <Input
              placeholder="e.g. Forex Factory"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Search</label>
            <Input
              placeholder="Search headlines & content"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        {availableTags.length > 0 && (
          <div>
            <label className="block text-sm mb-1">Tags</label>
            <TagMultiSelect
              availableTags={availableTags}
              selectedTags={selectedTags}
              onChange={setSelectedTags}
            />
          </div>
        )}
        <div className="flex gap-2">
          <Button onClick={() => { setPage(1); fetchData({ page: 1 }); }}>Apply</Button>
          <Button variant="secondary" onClick={clearFilters}>Clear</Button>
        </div>
      </Card>

      {/* News list */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-secondary/50">
              <tr>
                <th className="text-left p-3">Time</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Headline</th>
                <th className="text-left p-3">Tags</th>
                <th className="text-left p-3">Link</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td className="p-3" colSpan={5}>Loading...</td></tr>
              )}
              {error && !loading && (
                <tr><td className="p-3 text-red-400" colSpan={5}>{error}</td></tr>
              )}
              {!loading && !error && articles.length === 0 && (
                <tr><td className="p-3" colSpan={5}>No news articles found</td></tr>
              )}
              {!loading && !error && articles.map((article) => {
                const isExpanded = expandedIds.has(article.id);
                const hasContent = !!article.content;
                const plainPreview = stripHtml(article.content);
                const sentimentScore = formatMetric(article.sentiment_score, { digits: 2, signed: true });
                const impactAmount = formatMetric(article.impact_amount, { digits: 2, signed: true });
                const confidence = formatMetric(article.enrichment_confidence, { digits: 2 });
                const psychologyScore = formatMetric(article.psychological_impact_score, { digits: 2, signed: true });
                const psychologicalText = stripHtml(article.psychological_impact_text);
                const symbolSummary = compactList(article.impacted_symbols);
                const countrySummary = compactList(article.impacted_countries);
                const industrySummary = compactList(article.impacted_industries);
                const hasAiSummary = Boolean(
                  article.sentiment_direction ||
                  sentimentScore ||
                  impactAmount ||
                  confidence ||
                  symbolSummary ||
                  countrySummary ||
                  industrySummary ||
                  psychologyScore
                );

                return (
                  <React.Fragment key={article.id}>
                    <tr
                      className={`border-t border-border/40 hover:bg-secondary/20 ${hasContent ? "cursor-pointer" : ""}`}
                      onClick={() => hasContent && toggleExpanded(article.id)}
                    >
                      <td className="p-3 whitespace-nowrap">{formatTime(article.time)}</td>
                      <td className="p-3 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs">
                          {article.source || "-"}
                        </span>
                      </td>
                      <td className="p-3 min-w-[420px]">
                        <div className="max-w-4xl">
                          <div className="flex items-start gap-2">
                            <p className="flex-1 text-sm font-semibold leading-6 sm:text-[15px]">{article.headline || "-"}</p>
                            {hasContent && (
                              <span className="mt-0.5 text-textSecondary shrink-0">
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </span>
                            )}
                          </div>
                          {!isExpanded && plainPreview && (
                            <p className="text-textSecondary text-xs mt-1 line-clamp-2">{plainPreview}</p>
                          )}
                          {hasAiSummary ? (
                            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                              {article.sentiment_direction ? (
                                <span className={`inline-flex rounded-full border px-2 py-0.5 font-medium ${sentimentTone(article.sentiment_direction)}`}>
                                  {labelize(article.sentiment_direction)}{sentimentScore ? ` ${sentimentScore}` : ""}
                                </span>
                              ) : null}
                              {impactAmount || confidence ? (
                                <span className="inline-flex rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-primary">
                                  Impact {impactAmount || "-"}{confidence ? ` · AI ${confidence}` : ""}
                                </span>
                              ) : null}
                              {symbolSummary ? (
                                <span className="text-textSecondary">
                                  <span className="text-textPrimary">Symbols:</span> {symbolSummary}
                                </span>
                              ) : null}
                              {!symbolSummary && countrySummary ? (
                                <span className="text-textSecondary">
                                  <span className="text-textPrimary">Countries:</span> {countrySummary}
                                </span>
                              ) : null}
                              {industrySummary ? (
                                <span className="text-textSecondary">
                                  <span className="text-textPrimary">Industries:</span> {industrySummary}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </td>
                      <td className="p-3">
                        <ValuePills values={article.tags} maxVisible={5} />
                      </td>
                      <td className="p-3">
                        {article.url ? (
                          <a
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline text-xs"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Open &rarr;
                          </a>
                        ) : "-"}
                      </td>
                    </tr>
                    {isExpanded && hasContent && (
                      <tr className="bg-secondary/10">
                        <td colSpan={5} className="px-6 py-4">
                          <div className="grid gap-3 lg:grid-cols-[1.2fr_1fr_1fr] mb-4">
                            <div className="rounded-lg border border-border/40 bg-background/40 p-3">
                              <p className="text-[11px] uppercase tracking-[0.18em] text-textSecondary">AI Snapshot</p>
                              <div className="mt-2 space-y-2">
                                {article.sentiment_direction ? (
                                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${sentimentTone(article.sentiment_direction)}`}>
                                    {labelize(article.sentiment_direction)}
                                  </span>
                                ) : null}
                                <p className="text-sm text-textPrimary">Score: {sentimentScore || "-"}</p>
                                <p className="text-sm text-textPrimary">Impact: {impactAmount || "-"}</p>
                                <p className="text-sm text-textSecondary">AI confidence: {confidence || "-"}</p>
                                <div>
                                  <p className="text-xs text-textSecondary mb-1">Tags</p>
                                  <ValuePills values={article.tags} maxVisible={8} />
                                </div>
                              </div>
                            </div>
                            <div className="rounded-lg border border-border/40 bg-background/40 p-3">
                              <p className="text-[11px] uppercase tracking-[0.18em] text-textSecondary">Affected Markets</p>
                              <div className="mt-2 space-y-2">
                                <div>
                                  <p className="text-xs text-textSecondary mb-1">Symbols</p>
                                  <ValuePills values={article.impacted_symbols} tone="primary" maxVisible={6} />
                                </div>
                                <div>
                                  <p className="text-xs text-textSecondary mb-1">Countries</p>
                                  <ValuePills values={article.impacted_countries} maxVisible={6} />
                                </div>
                                <div>
                                  <p className="text-xs text-textSecondary mb-1">Industries</p>
                                  <ValuePills values={article.impacted_industries} maxVisible={6} />
                                </div>
                              </div>
                            </div>
                            <div className="rounded-lg border border-border/40 bg-background/40 p-3">
                              <p className="text-[11px] uppercase tracking-[0.18em] text-textSecondary">Psychological Impact</p>
                              <div className="mt-2 space-y-2">
                                <p className="text-sm text-textPrimary">Score: {psychologyScore || "-"}</p>
                                <p className="text-xs text-textSecondary leading-relaxed">
                                  {psychologicalText || "No psychological commentary generated."}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div
                            className="prose prose-sm prose-invert max-w-none text-sm text-textSecondary leading-relaxed
                              [&_a]:text-primary [&_a]:underline
                              [&_img]:max-w-full [&_img]:rounded
                              [&_ul]:list-disc [&_ul]:pl-5
                              [&_ol]:list-decimal [&_ol]:pl-5
                              [&_p]:mb-2
                              [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mb-2
                              [&_h2]:text-base [&_h2]:font-bold [&_h2]:mb-2
                              [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mb-1
                              [&_blockquote]:border-l-2 [&_blockquote]:border-primary/40 [&_blockquote]:pl-3 [&_blockquote]:italic"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.content) }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
            <span className="text-sm text-textSecondary">
              {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()} articles
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => goToPage(1)}
                className="px-2"
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                className="px-2"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {(() => {
                const pages = [];
                let start = Math.max(1, page - 2);
                let end = Math.min(totalPages, start + 4);
                start = Math.max(1, end - 4);
                for (let i = start; i <= end; i++) {
                  pages.push(
                    <Button
                      key={i}
                      variant={i === page ? "default" : "ghost"}
                      size="sm"
                      className="px-3 min-w-[2rem]"
                      onClick={() => goToPage(i)}
                    >
                      {i}
                    </Button>
                  );
                }
                return pages;
              })()}
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
                className="px-2"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => goToPage(totalPages)}
                className="px-2"
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
